import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi} from 'api';
import {DataWrapper, InformationModal, Wizard, ChargebeePaymentStep} from 'components';
import {CHARGEBEE_CLIENT_SCRIPT} from 'constants/billing-platform';
import {PAYMENT_SOURCE_ADDED} from 'constants/images';
import {useApiData, useExternalScript} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.PAYMENT_SOURCE.ADD_PAYMENT_MODAL;
const T_KEYS_WIZARD_NAV = TRANSLATION_KEYS.COMPONENTS.BILLING_PLATFORM_WIZARD_NAV;

type Props = {
    open: boolean;
    loading?: boolean;
    onClose: (success: boolean) => void;
};

export const AddPaymentSourceModal: React.FC<Props> = ({open, loading, onClose}) => {
    const {t} = useTranslation();
    const [wizardInstance, setWizardInstance] = useState(null);

    const {
        send: fetchBrowserCredentials,
        data: credentials,
        error: credentialsError,
        reset: resetBrowserCredentials
    } = useApiData(billingPlatformApi.getBrowserCredentials);

    const {loaded: scriptLoaded} = useExternalScript(CHARGEBEE_CLIENT_SCRIPT);

    const navItems = useMemo(() => [
        t(T_KEYS_WIZARD_NAV.CARD_INFO),
        t(T_KEYS_WIZARD_NAV.DONE)
    ], [t]);

    const handleClose = useCallback(() => {
        const cardAddedSuccessfully = wizardInstance && wizardInstance.currentStep === wizardInstance.totalSteps;
        return onClose(cardAddedSuccessfully);
    }, [onClose, wizardInstance]);

    useEffect(() => {
        if (scriptLoaded) {
            fetchBrowserCredentials();
            return () => {
                resetBrowserCredentials();
            };
        }
    }, [scriptLoaded, fetchBrowserCredentials, resetBrowserCredentials]);

    return (
        <InformationModal maxWidth='sm' fullWidth title={t(T_KEYS.TITLE)} open={open} onClose={handleClose}>
            <DataWrapper isError={!!credentialsError} isLoading={loading || !scriptLoaded}>
                <Wizard instance={setWizardInstance} navItems={navItems}>
                    <ChargebeePaymentStep
                        credentials={credentials}
                        noImmediateCharge
                        wizardFooterProps={{
                            hasNext: true
                        }}
                        paymentAddedCallback={billingPlatformApi.addPaymentSource}
                    />
                    <div className='tw-text-center'>
                        <div className='tw-ts-h5-bold tw-mb-4'>{t(T_KEYS.DONE.TITLE)}</div>
                        <div className='tw-mb-8'>{t(T_KEYS.DONE.MESSAGE)}</div>
                        <img src={PAYMENT_SOURCE_ADDED} alt={t(T_KEYS.DONE.TITLE)} />
                    </div>
                </Wizard>
            </DataWrapper>
        </InformationModal>
    );
};